import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import PayrollModal from './PayrollModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Payrolls {
  payroll_id: number;
  payroll_check_in: string;
  payroll_check_out: string;
  payroll_working_hours: string;
  payroll_days_of_working: string;
  payroll_days_off: string;
  payroll_date_of_joining: string;
  payroll_salary: number;
  payroll_type_of_work: string;
  payroll_increment_period: string;
  payroll_increment_type: string;
  payroll_status: number;
}

const Payroll: React.FC = () => {
  const [payrolls, setPayrolls] = useState<Payrolls[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Payrolls | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchPayroll = async () => {
    try {
      const data = await apiRequest<Payrolls[]>({
        method: 'get',
        url: 'payrolls',
      });
      setPayrolls(data);
    } catch (error) {
      showError('Failed to fetch Payrolls');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPayroll();
  }, []);

  const handleEditClick = (record: Payrolls) => {
    setIsEditMode(true);
    setInitialValues({
        payroll_id: record.payroll_id,
        payroll_check_in: record.payroll_check_in,
        payroll_check_out: record.payroll_check_out,
        payroll_working_hours: record.payroll_working_hours,
        payroll_days_of_working: record.payroll_days_of_working,
        payroll_days_off: record.payroll_days_off,
        payroll_date_of_joining: record.payroll_date_of_joining,
        payroll_salary: record.payroll_salary,
        payroll_type_of_work: record.payroll_type_of_work,
        payroll_increment_period: record.payroll_increment_period,
        payroll_increment_type: record.payroll_increment_type,
        payroll_status: record.payroll_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchPayroll();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (payroll_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-payroll/${payroll_id}`,
      });
      showSuccess('Payroll deleted');
      fetchPayroll();
    } catch {
      showError('Failed to delete Payroll');
    }
  };

  const handleStatusToggle = async (payrollId: number) => {
    setPayrolls((prev) =>
      prev.map((item) =>
        item.payroll_id === payrollId
          ? { ...item, payroll_status: item.payroll_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-payroll/${payrollId}`,
      });
      showSuccess('Payroll status updated');
    } catch (error) {
      showError('Failed to update status');
      setPayrolls((prev) =>
        prev.map((item) =>
          item.payroll_id === payrollId
            ? { ...item, payroll_status: item.payroll_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Payrolls>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Payrolls> = [
    {
    title: 'Check In',
    dataIndex: 'payroll_check_in',
    key: 'payroll_check_in',
    ellipsis: true,
    width: 100,
     render: (text) => <span>{text || '-'}</span>,
    },
    {
    title: 'Check Out',
    dataIndex: 'payroll_check_out',
    key: 'payroll_check_out',
    ellipsis: true,
    width: 110,
    // render: (_, record) => {
    //     return <div>{record.payroll_check_out}</div>; // No need to convert
    // },
     render: (text) => <span>{text || '-'}</span>,
    },
    {
    title: (
        <>
        Working <br /> Hours
        </>
    ),
    dataIndex: 'payroll_working_hours',
    key: 'payroll_working_hours',
    width: 120,
    ellipsis: true,
    render: (_, record) => <div>{record.payroll_working_hours}</div>,
    },
    {
    title: "Days of Working",
    dataIndex: 'payroll_days_of_working',
    key: 'payroll_days_of_working',
    width: 200,
    ellipsis: true,
    render: (_, record) => <div>{record.payroll_days_of_working}</div>,
    },
    {
    title: (
        <>
        Day <br /> Off
        </>
    ),
    dataIndex: 'payroll_days_off',
    key: 'payroll_days_off',
    width: 120,
    filters: Array.from(new Set(payrolls.map((s) => s.payroll_days_off))).map((name) => ({
        text: name,
        value: name,
    })),
    filterSearch: true,
    filteredValue: filteredInfo.payroll_days_off || null,
    onFilter: (value, record) => record.payroll_days_off === value,
    sorter: (a, b) => a.payroll_days_off.localeCompare(b.payroll_days_off),
    sortOrder: sortedInfo.columnKey === 'payroll_days_off' ? sortedInfo.order : null,
    ellipsis: true,
    render: (_, record) => <div>{record.payroll_days_off}</div>,
    },
    {
        title: 'Date of Joining',
        dataIndex: 'payroll_date_of_joining',
        key: 'payroll_date_of_joining',
        width: 120,
        sorter: (a, b) => new Date(a.payroll_date_of_joining).getTime() - new Date(b.payroll_date_of_joining).getTime(),
        sortOrder: sortedInfo.columnKey === 'payroll_date_of_joining' ? sortedInfo.order : null,
        render: (_, record) => {
        const date = new Date(record.payroll_date_of_joining).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            timeZone: 'Asia/Kolkata', // optional if needed
        });
    return <div>{date}</div>;
        }
    },
    {
    title: 'Salary',
    dataIndex: 'payroll_salary',
    key: 'payroll_salary',
    width: 120,
    sorter: (a, b) => a.payroll_salary - b.payroll_salary,
    sortOrder: sortedInfo.columnKey === 'payroll_salary' ? sortedInfo.order : null,
    render: (_, record) => <div>{record.payroll_salary}</div>,
    },
    {
    title: (
        <>
        Type of <br /> Work
        </>
    ),
    dataIndex: 'payroll_type_of_work',
    key: 'payroll_type_of_work',
    width: 160,
    filters: Array.from(new Set(payrolls.map((s) => s.payroll_type_of_work))).map((name) => ({
        text: name,
        value: name,
    })),
    filterSearch: true,
    filteredValue: filteredInfo.payroll_type_of_work || null,
    onFilter: (value, record) => record.payroll_type_of_work.includes(value as string),
    sorter: (a, b) => a.payroll_type_of_work.localeCompare(b.payroll_type_of_work),
    sortOrder: sortedInfo.columnKey === 'payroll_type_of_work' ? sortedInfo.order : null,
    render: (_, record) => <div>{record.payroll_type_of_work}</div>,
    },
    {
    title:(
        <>
        Increment <br /> Period
        </>
    ),
    dataIndex: 'payroll_increment_period',
    key: 'payroll_increment_period',
    width: 160,
    filters: Array.from(new Set(payrolls.map((s) => s.payroll_increment_period))).map((name) => ({
        text: name,
        value: name,
    })),
    filterSearch: true,
    filteredValue: filteredInfo.payroll_increment_period || null,
    onFilter: (value, record) => record.payroll_increment_period.includes(value as string),
    sorter: (a, b) => a.payroll_increment_period.localeCompare(b.payroll_increment_period),
    sortOrder: sortedInfo.columnKey === 'payroll_increment_period' ? sortedInfo.order : null,
    render: (_, record) => <div>{record.payroll_increment_period}</div>,
    },
    {
    title: (
        <>
        Increment <br /> Type
        </>
    ),
    dataIndex: 'payroll_increment_type',
    key: 'payroll_increment_type',
    width: 130,
    filters: Array.from(new Set(payrolls.map((s) => s.payroll_increment_type))).map((name) => ({
        text: name,
        value: name,
    })),
    filterSearch: true,
    filteredValue: filteredInfo.payroll_increment_type || null,
    onFilter: (value, record) => record.payroll_increment_type.includes(value as string),
    sorter: (a, b) => a.payroll_increment_type.localeCompare(b.payroll_increment_type),
    sortOrder: sortedInfo.columnKey === 'payroll_increment_type' ? sortedInfo.order : null,
    render: (_, record) => <div>{record.payroll_increment_type}</div>,
    }

  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      fixed: 'right', 
      render: (_: any, record: Payrolls) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.payroll_status}
                onChange={() => handleStatusToggle(record.payroll_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this Payroll?"
              onConfirm={() => handleDelete(record.payroll_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Payroll</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Payroll
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Payrolls>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="payroll_id"
          dataSource={payrolls.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
            // tableLayout="fixed" 
          scroll={{ x: 1900, y: 37 * 13 }}
          locale={{ emptyText: 'No Payroll found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, payrolls.length)} of{' '}
                {payrolls.length}
              </span>
              <Pagination
                {...pagination}
                total={payrolls.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <PayrollModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Payroll;